// BathroomPreview.js
import React, { useRef, useEffect, useState } from 'react';
import {
  Engine,
  Scene,
  ArcRotateCamera,
  Vector3,
  VertexBuffer,
  Color4,
  SceneLoader,
  StandardMaterial,
  TransformNode,
  Mesh,
} from '@babylonjs/core';
import '@babylonjs/loaders';
import { Color3 } from '@babylonjs/core';

// (1) Our external PBR factory
import {
  createWhiteCeramicMaterial,
  createWhiteDoorMaterial,
  createGreyQuartzStoneTileMaterial,
  createBlackCeramicHexagonMaterial,
} from './bathroomMaterials';

// (2) Axes & grid
import { showAxes, showGridWithLabels } from './gridAndLabels';

// (3) Mirror setup
import { setupMirror } from './mirrorSetup';

// (4) Lighting setup
import { setupLighting } from './lighting';

// (5) Door frame logic
import { createDoorFrame, matchDoorFrameToDoor } from './doorFrame';

// (6) Ceiling logic (the new file)
import { createCeiling } from './createCeiling';

// Layout data & model url map
const modelUrlMap = {
  'toilet.glb':
    'https://yuxbcsdfnhttlrmzsqvz.supabase.co/storage/v1/object/public/products/toilet.glb',
  'vanity.glb':
    'https://yuxbcsdfnhttlrmzsqvz.supabase.co/storage/v1/object/public/products/vanity.glb',
  'shower.glb':
    'https://yuxbcsdfnhttlrmzsqvz.supabase.co/storage/v1/object/public/products/shower.glb',
};

const layoutData = {
  layout_id: 99,
  placements: [
    {
      name: 'vanity',
      center: [0.5796, -0.9858],
      parentWall: '1D3EC343-7E07-4FE9-97FA-4DEEACE2EE3F',
      rotationFromXAxis: 45.898939,
      width: 1.5,
      depth: 0.521,
      height: 0.856,
      heightAboveFloor: 0.6,
      frontCenterCoords: [0.3925, -0.8045],
      backCenterCoords: [0.7666, -1.1671],
    },
    {
      name: 'toilet',
      center: [-0.6764, -0.9838],
      parentWall: '032E544D-3D65-4F5F-92F9-DE591D8A7C73',
      rotationFromXAxis: -44.1010567,
      width: 0.37,
      depth: 0.6,
      height: 0.8,
      heightAboveFloor: 0.5,
      frontCenterCoords: [-0.4676, -0.7683],
      backCenterCoords: [-0.8852, -1.1992],
    },
    {
      name: 'shower',
      center: [-1.5567, 0.2486],
      parentWall: '12E39FDC-023B-4D1D-8186-65835B44127A',
      rotationFromXAxis: -134.1010515,
      width: 0.7,
      depth: 0.9,
      height: 1.0,
      heightAboveFloor: 1.0,
      frontCenterCoords: [-1.2335, -0.0645],
      backCenterCoords: [-1.8798, 0.5618],
    },
  ],
  downlights: [
    [0.6037, -0.6167],
    [-1.5284, 0.0569],
    [-0.0921, -1.3348],
    [-0.1143, 0.0791],
  ],
};

const BathroomPreview = () => {
  const canvasRef = useRef(null);
  const sceneRef = useRef(null);

  // Floor & ceiling
  const [floorYCoord, setFloorYCoord] = useState(0);
  const [ceilingYCoord, setCeilingYCoord] = useState(0);

  // Track that the main bathroom model is loaded
  const [bathroomLoaded, setBathroomLoaded] = useState(false);

  // Track that additional objects are loaded
  const [objectsLoaded, setObjectsLoaded] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      console.warn('No canvas reference found. Returning early.');
      return;
    }

    const engine = new Engine(canvas, true);
    const scene = new Scene(engine);
    sceneRef.current = scene;

    // Use a right‐handed system
    scene.useRightHandedSystem = true;

    // Scene background
    scene.clearColor = new Color4(0.8, 0.8, 0.8, 1);

    // Camera
    const camera = new ArcRotateCamera(
      'camera',
      Math.PI / 4,
      Math.PI / 3,
      6,
      Vector3.Zero(),
      scene
    );
    camera.wheelPrecision = 200;
    camera.attachControl(canvas, true);

    // (B) Load PBR materials
    const whiteCeramicMaterial = createWhiteCeramicMaterial(scene);
    const whiteDoorMaterial = createWhiteDoorMaterial(scene);
    const greyQuartzStoneMaterial = createGreyQuartzStoneTileMaterial(scene);
    const blackCeramicHexagonMaterial = createBlackCeramicHexagonMaterial(scene);

    // (C) Load the main bathroom model
    const bathroomModelUrl =
      'https://yuxbcsdfnhttlrmzsqvz.supabase.co/storage/v1/object/public/products/bathroom.glb';

    SceneLoader.ImportMesh('', bathroomModelUrl, '', scene, (meshes) => {
      if (!meshes || meshes.length === 0) {
        console.warn('No bathroom meshes loaded');
        return;
      }

      let maxYAmongAll = Number.NEGATIVE_INFINITY;
      let floorMesh = null; // We'll store "floor0" here

      meshes.forEach((mesh) => {
        // Ensure UVs
        if (!mesh.isVerticesDataPresent(VertexBuffer.UVKind)) {
          assignBoxUVsRealWorld(mesh, 0.5);
        }

        // By default, let's give walls a ceramic material
        mesh.material = whiteCeramicMaterial;

        // Example override for "wall2"
        if (mesh.name.toLowerCase().includes('wall2')) {
          mesh.material = blackCeramicHexagonMaterial;
        }

        // If it's the door, apply door frame logic
        if (mesh.name.toLowerCase().includes('door')) {
          mesh.material = whiteDoorMaterial;

          mesh.computeWorldMatrix(true);
          mesh.refreshBoundingInfo();
          const bb = mesh.getBoundingInfo().boundingBox;
          const doorWidth = bb.maximum.x - bb.minimum.x;
          const doorHeight = bb.maximum.y - bb.minimum.y;
          const doorDepth = bb.maximum.z - bb.minimum.z;

          const frame = createDoorFrame(scene, doorWidth, doorHeight, doorDepth);
          matchDoorFrameToDoor(mesh, frame);
        }

        // Identify the floor
        if (mesh.name.toLowerCase().includes('floor0')) {
          mesh.material = greyQuartzStoneMaterial;
          mesh.computeWorldMatrix(true);
          floorMesh = mesh;

          // We can store the floor's Y
          const floorBB = mesh.getBoundingInfo().boundingBox;
          setFloorYCoord(floorBB.maximumWorld.y);
        }

        // Track highest Y => for the ceiling
        mesh.computeWorldMatrix(true);
        const { maximumWorld } = mesh.getBoundingInfo().boundingBox;
        if (maximumWorld.y > maxYAmongAll) {
          maxYAmongAll = maximumWorld.y;
        }
      });

      // Record the highest Y among all geometry
      setCeilingYCoord(maxYAmongAll);

      // ---------------------------------
      // CREATE THE CEILING via new helper
      // ---------------------------------
      if (floorMesh) {
        createCeiling(scene, floorMesh, maxYAmongAll, assignBoxUVsRealWorld);
      }

      // Now that everything is set, do lighting, etc.
      setupLighting(scene, layoutData, maxYAmongAll);
      setBathroomLoaded(true);
    });

    // engine loop
    engine.runRenderLoop(() => {
      scene.render();
    });

    return () => {
      scene.dispose();
      engine.dispose();
    };
  }, []);

  // Load additional objects (toilet, vanity, shower, etc.)
  useEffect(() => {
    if (!sceneRef.current || !bathroomLoaded || objectsLoaded) return;

    layoutData.placements.forEach((pl) => {
      const nameLower = pl.name.toLowerCase();
      const glbFileName = `${nameLower}.glb`;
      loadAdditionalObject(glbFileName, pl);
    });

    setObjectsLoaded(true);
  }, [bathroomLoaded, objectsLoaded]);

  // After everything is loaded, create the mirror
  useEffect(() => {
    if (!sceneRef.current) return;
    if (!bathroomLoaded || !objectsLoaded) return;

    createVanityMirror(sceneRef.current);
  }, [bathroomLoaded, objectsLoaded]);

  // -------------------------------------
  //   HELPER: Create vanity mirror
  // -------------------------------------
  function createVanityMirror(scene) {
    const vanityPlacement = layoutData.placements.find((p) =>
      p.name.toLowerCase().includes('vanity')
    );
    if (!vanityPlacement) return;

    const mirrorPosition = new Vector3(
      vanityPlacement.backCenterCoords[0],
      floorYCoord + 1.6,
      vanityPlacement.backCenterCoords[1]
    );
    const mirrorLookToPosition = new Vector3(
      vanityPlacement.frontCenterCoords[0],
      floorYCoord + 1.6,
      vanityPlacement.frontCenterCoords[1]
    );

    setTimeout(() => {
      setupMirror(scene, mirrorPosition, mirrorLookToPosition);
    }, 3000);
  }

  // -------------------------------------
  //   HELPER: Load objects
  // -------------------------------------
  function loadAdditionalObject(modelFileName, placement = null) {
    if (!sceneRef.current) return;
    const scene = sceneRef.current;

    const modelUrlToLoad = modelUrlMap[modelFileName];
    if (!modelUrlToLoad) {
      console.warn(`No URL mapped for ${modelFileName}. Cannot load object.`);
      return;
    }

    SceneLoader.ImportMesh(
      null,
      '',
      modelUrlToLoad,
      scene,
      (meshes) => {
        if (!meshes || meshes.length === 0) {
          console.warn(`No meshes found in model ${modelFileName}.`);
          return;
        }

        const rootMesh = meshes[0];
        meshes.forEach((m) => {
          if (m !== rootMesh) {
            m.setParent(rootMesh);
          }
        });

        // Create a pivot node
        const pivotNode = new TransformNode(`pivotNode_${modelFileName}`, scene);
        rootMesh.setParent(pivotNode);
        rootMesh.computeWorldMatrix(true);

        if (placement) {
          const yOffset = placement.heightAboveFloor || 0;
          pivotNode.position.set(
            placement.backCenterCoords[0],
            floorYCoord + yOffset,
            placement.backCenterCoords[1]
          );

          // Aim pivot toward frontCenterCoords horizontally
          if (placement.frontCenterCoords) {
            const [fx, fz] = placement.frontCenterCoords;
            const pivotPos = pivotNode.position.clone();
            pivotNode.lookAt(new Vector3(fx, pivotPos.y, fz), 0, 1, 0);
            pivotNode.rotation.x = 0;
            pivotNode.rotation.z = 0;
          }
        }
      },
      null,
      (scene, msg, exc) => {
        console.error(`Error loading additional object: ${modelFileName}`, msg, exc);
      }
    );
  }

  // -------------------------------------
  //   HELPER: Assign real‐world UVs
  // -------------------------------------
  function assignBoxUVsRealWorld(mesh, tileSize = 0.5) {
    const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
    if (!positions) return;

    let normals = mesh.getVerticesData(VertexBuffer.NormalKind);
    if (!normals) {
      mesh.forceSharedVertices();
      mesh.computeNormals(true);
      normals = mesh.getVerticesData(VertexBuffer.NormalKind);
    }

    const uv = [];
    for (let i = 0; i < positions.length; i += 3) {
      const px = positions[i];
      const py = positions[i + 1];
      const pz = positions[i + 2];
      const nx = normals[i];
      const ny = normals[i + 1];
      const nz = normals[i + 2];

      const absNx = Math.abs(nx);
      const absNy = Math.abs(ny);
      const absNz = Math.abs(nz);

      let u = 0;
      let v = 0;

      // Decide which face to project
      if (absNx >= absNy && absNx >= absNz) {
        // ±X face => map (z, y) -> (u, v)
        u = pz / tileSize;
        v = py / tileSize;
      } else if (absNy >= absNx && absNy >= absNz) {
        // ±Y face => map (x, z) -> (u, v)
        u = px / tileSize;
        v = pz / tileSize;
      } else {
        // ±Z face => map (x, y) -> (u, v)
        u = px / tileSize;
        v = py / tileSize;
      }
      uv.push(u, v);
    }
    mesh.setVerticesData(VertexBuffer.UVKind, uv, true);
  }

  // -------------------------------------
  //   Render
  // -------------------------------------
  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <p>Floor Y: {floorYCoord.toFixed(2)}</p>
        <p>Ceiling Y: {ceilingYCoord.toFixed(2)}</p>
      </div>
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: '900px', display: 'block' }}
      />
    </div>
  );
};

export default BathroomPreview;
